import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface NewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewsletterModal = ({ isOpen, onClose }: NewsletterModalProps) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const newsletterMutation = useMutation({
    mutationFn: async ({ firstName, lastName, email }: { firstName: string; lastName: string; email: string }) => {
      return apiRequest('/api/mailchimp/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ firstName, lastName, email, audienceId: 'a213512fa7' })
      });
    },
    onSuccess: () => {
      toast({
        title: "Welcome to the darkness!",
        description: "You've successfully joined the mailing list.",
      });
      setFirstName("");
      setLastName("");
      setEmail("");
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Subscription failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && lastName.trim() && email.trim()) {
      newsletterMutation.mutate({ firstName, lastName, email });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gothic-charcoal border-gothic-gray max-w-md" data-testid="newsletter-modal">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle className="text-2xl font-playfair font-bold text-blood-red">
              Join the Light
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-gothic-white hover:bg-transparent"
              data-testid="newsletter-modal-close"
              aria-label="Close newsletter modal"
            >
              <X size={20} />
            </Button>
          </div>
        </DialogHeader>
        <div className="mt-4">
          <p className="text-gray-300 mb-6">
            Get exclusive updates on new releases, horror writing tips, and behind-the-scenes content.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1">
                First Name
              </label>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                className="bg-gothic-black border-gothic-gray text-white focus:border-blood-red focus:ring-blood-red"
                data-testid="newsletter-modal-firstname"
                required
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1">
                Last Name
              </label>
              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                className="bg-gothic-black border-gothic-gray text-white focus:border-blood-red focus:ring-blood-red"
                data-testid="newsletter-modal-lastname"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="bg-gothic-black border-gothic-gray text-white focus:border-blood-red focus:ring-blood-red"
                data-testid="newsletter-modal-email"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={newsletterMutation.isPending}
              className="w-full bg-blood-red hover:bg-deep-red text-white font-semibold"
              data-testid="newsletter-modal-submit"
              aria-label="Subscribe to Diane Corso's newsletter"
            >
              {newsletterMutation.isPending ? "Subscribing..." : "send me my free book"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterModal;